"use client";
import { avenirNextMedium, avenirNextRegular } from '@/styles/fonts';
import clsx from 'clsx';
import React, { useState } from 'react';
import { cardButtonBlueClass } from './pricingButton';

interface PricingFAQProps {
    countries: Array<{
        countryId: string,
        countryName: string
    }>,
    networks: Array<{
        isNative: boolean,
        networkId: string,
        networkName: string
    }>
}

const faqItemClass = clsx(
    "text-[#869ca3]",
    "text-[14px]",
    "sm:text-[16px]",
    "leading-[20px]",
    "sm:leading-[30px]",
    "sm:mt-[10px]",
);

export default function PricingFAQ(props: PricingFAQProps) {
    const allListedNetworks = props.networks
        .filter(network => network.networkId !== 'all');
    const nativeNetworks = allListedNetworks
        .filter(network => network.isNative);
    const totalNetworks = allListedNetworks.length;
    const totalNativeNetworks = nativeNetworks.length;
    const nativeNetworkNames = nativeNetworks.map(network => network.networkName);
    nativeNetworkNames[nativeNetworkNames.length - 1] =
        `and ${nativeNetworkNames[nativeNetworkNames.length - 1]}`;

    const countryNames = props.countries
        .filter(country => country.countryId !== 'all')
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map(country => country.countryName);
    countryNames[countryNames.length - 1] = `and ${countryNames[countryNames.length - 1]}`;

    const [isNetworkListOpen, setIsNetworkListOpen] = useState(false);
    const [openSections, setOpenSections] = useState<number[]>([0]);

    const toggleSection = (index: number) => {
        setOpenSections(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    // Update helper functions to check if a section is open
    const getMaxHeightClass = (index: number) => {
        return openSections.includes(index) ? "max-h-auto" : "max-h-0";
    };

    const getVisibilityClass = (index: number) => {
        return openSections.includes(index) ? "opacity-100" : "opacity-0";
    };

    const toggleIconClass = clsx(
        "bg-no-repeat",
        "bg-center",
        "bg-contain",
        "bg-[length:18px_18px]",
        "w-[18px]",
        "h-[18px]",
        "shrink-0",
    );

    return (
        <>
            <h2 className={clsx(
                "w-[95%]",
                'md:w-[87%]',
                'xl:w-[64%]',
                "max-w-[1160px]",
                "mt-[60px]",
                "mx-auto",
                avenirNextMedium.className,
                "text-[22px] sm:text-[26px] md:text-[26px] text-[#52575b] font-[500] mb-[30px]",
            )}>Frequently Asked Questions</h2>
            <div className={clsx(
                "w-[95%]",
                'md:w-[87%]',
                'xl:w-[64%]',
                "max-w-[1160px]",
                "mx-auto",
                "rounded-[6px]",
                "mb-[80px]",
                "bg-white",
                "p-[15px]",
                "md:p-[25px]",
                "shadow-[0px_4px_6px_rgba(0,0,0,0.1)]",
                avenirNextRegular.className,
                "text-[#869ca3]",
                "text-[16px]",
                "leading-[28px]",
            )}>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(0)}>
                            <div>
                                Which networks does Adbeat cover?
                            </div>
                            <div className={clsx(
                                openSections.includes(0)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(0),
                        getVisibilityClass(0)
                    )}>
                        Adbeat leads the industry with comprehensive coverage across {totalNetworks} of the most important display networks, DSPs and exchanges. New networks are added regularly. We also have coverage for Direct Buys (ad placements purchased directly from publisher sites that bypass networks and exchanges).
                        <div>
                            <span className={clsx(cardButtonBlueClass({
                                textSize: "text-[12px]",
                                paddingY: "py-[1px]",
                                paddingX: "px-[5px]",
                                width: "w-[105px]",
                            }), "cursor-pointer")}
                                onClick={() => setIsNetworkListOpen(!isNetworkListOpen)}>
                                See all networks
                            </span>
                        </div>

                        <div className={clsx(
                            faqItemClass,
                            "my-[10px]",
                            "grid",
                            "grid-cols-2",
                            "md:grid-cols-3",
                            "lg:grid-cols-4",
                            "gap-x-[20px]",
                            "gap-y-[10px]",
                            isNetworkListOpen ? "block" : "hidden",
                        )}>
                            {allListedNetworks.map(network => (
                                <div key={network.networkId}>{network.networkName}</div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(1)}>
                            <div>
                                Which native networks does Adbeat cover?
                            </div>
                            <div className={clsx(
                                openSections.includes(1)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(1),
                        getVisibilityClass(1)
                    )}>
                        Adbeat covers {totalNativeNetworks} native ad networks: {nativeNetworkNames.join(', ')}
                    </div>
                </div>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(2)}>
                            <div>
                                Which countries and territories does Adbeat cover?
                            </div>
                            <div className={clsx(
                                openSections.includes(2)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(2),
                        getVisibilityClass(2)
                    )}>
                        {countryNames.join(', ')}
                    </div>
                </div>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(3)}>
                            <div>
                                What if I pick the wrong plan?
                            </div>
                            <div className={clsx(
                                openSections.includes(3)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(3),
                        getVisibilityClass(3)
                    )}>
                        You can change plans at any time all by yourself &mdash; just visit your User Profile page from inside the app. Or send an email to support@adbeat.com or use live chat anywhere on our site. We&apos;ll be happy to help.
                    </div>
                </div>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(4)}>
                            <div>
                                Can I get a free trial?
                            </div>
                            <div className={clsx(
                                openSections.includes(4)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(4),
                        getVisibilityClass(4)
                    )}>
                        We don&apos;t offer a free trial for Adbeat Pro plans. However, Adbeat Basic offers amazing insights and is free to use as long as you&apos;d like. You can upgrade to Adbeat Pro at any time, risk-free, thanks to our 30-day satisfaction guarantee.
                    </div>
                </div>

                <div className="mb-[20px]">
                    <div className={clsx(
                        avenirNextMedium.className,
                        "pb-[5px]",
                        "leading-[28px]",
                        "text-[#3a434e]",
                    )}>
                        <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection(5)}>
                            <div>
                                Can I get a refund if I&apos;m not happy?
                            </div>
                            <div className={clsx(
                                openSections.includes(5)
                                    ? "bg-[url('/public/images/icon-minus.png')]"
                                    : "bg-[url('/public/images/icon-plus.png')]",
                                toggleIconClass,
                            )}></div>
                        </div>
                    </div>
                    <div className={clsx(
                        faqItemClass,
                        "overflow-hidden",
                        "transition-all",
                        "duration-300",
                        getMaxHeightClass(5),
                        getVisibilityClass(5)
                    )}>
                        Yes. We offer a 30-day satisfaction guarantee. If you&apos;re not happy with your subscription during the first thirty days, just send an email to support@adbeat.com or use the live chat anywhere on our site and we&apos;ll issue a refund. No hassles, no hoops to jump through.
                    </div>
                </div>

            </div>
        </>
    );
}
